import * as R from 'ramda';
import { getIn } from 'formik';
import Dropzone from 'react-dropzone';
import { pure } from 'react-recompose';
import React, { Fragment } from 'react';
import PhoneInput from 'react-phone-number-input';
import flags from 'react-phone-number-input/flags';
// components
import { Switcher } from '../../../components/switcher';
import { TextComponent } from '../../../components/text';
import ColorPicker from '../../../components/color-picker';
// helpers/constants
import * as G from '../../../helpers';
import * as GC from '../../../constants';
import { PHONE_COUNTRIES } from '../../../helpers/options';
// icons
import * as I from '../../../svgs';
// ui
import { Toggle } from '../../restyled';
import { Box, Flex, Grid } from '../../../ui';
// features
import { CalendarInput } from '../../../features/new-do/forms/formik/calendar-time-input';
import {
  EditorComponent,
} from '../../../features/configurations/config-pages/communication/components/editor-component';
// forms
import CountrySelect from './country-select';
import PasswordInput from './password-input';
import MultiEmailInput from './multi-email-input';
import MultiItemsInput from './multi-items-input';
import { RadioGroup, RadioGroupMui } from './radio';
import PlaceAutocomplete from './place-autocomplete';
import { DateTimeInput, MaskedTimeInput } from './date-time-input';
import ReactSelectInput, { AsyncReactSelectInput } from './react-select-input';
import {
  InputWrapper,
  ToggleWrapper,
  CheckboxWrapper,
  InputWrapperWithClickZIndex,
} from './input-wrapper';
import {
  Input,
  Checkbox,
  Textarea,
  InputSelect,
  SelectWrapper,
  renderBorderColor,
  CalendarIconWrapper,
  CountrySelectWrapper,
  PhoneNumberInputWrapper,
} from './ui';
//////////////////////////////////////////////////

const renderOptions = (options) => {
  if (R.isNil(options)) return;

  return options.map((option, index) => {
    if (G.isString(option)) {
      return <option key={index} value={option}>{option}</option>;
    }

    const { name, guid, value, label, disabled } = option;

    return (
      <option key={index} disabled={disabled} value={R.or(value, guid)}>
        {R.or(name, label)}
      </option>
    );
  });
};

const getInputWrapperVisibility = (props) => {
  const visibility = R.path(['inputWrapperStyles', 'visibility'], props);

  if (G.isBoolean(visibility)) return visibility;

  if (G.isFunction(visibility)) return visibility(props);

  return undefined;
};

const setInputWrapperVisibilityAndDisplay = (props) => {
  const { inputWrapperStyles } = props;

  const visibility = getInputWrapperVisibility(props);
  const wrapperDisplay = R.path(['inputWrapperStyles', 'display'], props);

  if (G.isFunction(wrapperDisplay)) {
    const display = wrapperDisplay(props);

    return {
      ...inputWrapperStyles,
      display,
      visibility,
    };
  }

  return {
    ...inputWrapperStyles,
    visibility,
  };
};

const getInputWrapperStyles = (props) => {
  const { calcZIndex, arrayLength, rowMapIndex, fieldsetType } = props;

  if (R.and(G.isTrue(calcZIndex), R.equals(fieldsetType, 'array'))) {
    const zIndex = R.subtract(R.add(11, arrayLength), rowMapIndex);

    return {
      ...setInputWrapperVisibilityAndDisplay(props),
      zIndex,
    };
  }

  return setInputWrapperVisibilityAndDisplay(props);
};

const getIsRequired = (props) => {
  const { isRequired } = props;

  if (G.isFunction(isRequired)) {
    return isRequired(props);
  }

  return isRequired;
};

const getInfoText = (props) => {
  const { info, infoFunction } = props;

  if (G.isAllNilOrEmpty([info, infoFunction])) return null;

  if (G.isFunction(infoFunction)) return infoFunction(props);

  return info;
};

const FieldComponent = pure((props) => {
  const {
    name,
    type,
    label,
    value,
    title,
    error,
    options,
    errorTop,
    hasError,
    errorWidth,
    labelStyles,
    setFieldValue,
    setFieldTouched,
    withClickZIndex,
    showFieldConfig,
    isDesktop = true,
    hideErrorMessage,
    useMultiSelectMaxHeight,
    additionalLabelComponent,
  } = props;

  if (G.isNotNil(showFieldConfig)) {
    const showField = G.getAmousConfigByNameFromWindow(showFieldConfig);

    if (G.isFalse(showField)) return null;
  }

  const formikProps = R.pick([
    'values',
    'setValues',
    'setFieldError',
    'initialValues',
    'setFieldValue',
    'setFieldTouched',
  ], props);

  const simpleInputProps = R.pick([
    'id',
    'type',
    'name',
    'value',
    'onBlur',
    'onChange',
    'disabled',
    'isMobile',
    'hasError',
    'maxLength',
    'minHeight',
    'placeholder',
  ], props);

  const forceMaxWidth = R.path(['inputWrapperStyles', 'forceMaxWidth'], props);
  const mb = G.ifElse(isDesktop, R.path(['inputWrapperStyles', 'mb'], props), 0);
  const width = G.ifElse(isDesktop, R.path(['inputWrapperStyles', 'width'], props), '100%');
  const maxWidth = G.ifElse(isDesktop, R.path(['inputWrapperStyles', 'maxWidth'], props), R.or(forceMaxWidth, '100%'));

  const inputStyles = R.mergeRight({ width: '100%' }, R.pathOr({}, ['inputStyles'], props));

  const additionalLabelStyles = R.mergeRight({ top: -18, right: 5 }, R.pathOr({}, ['additionalLabelStyles'], props));

  const infoStyles = R.mergeRight(
    { ml: 5, width: 14, height: 14, bottom: 2, minWidth: 14 },
    R.pathOr({}, ['infoStyles'], props),
  );

  const wrapperStyles = {
    width,
    error,
    label,
    errorTop,
    hasError,
    infoStyles,
    errorWidth,
    labelStyles,
    hideErrorMessage,
    additionalLabelStyles,
    additionalLabelComponent,
    infoText: getInfoText(props),
    isRequired: getIsRequired(props),
    inputWrapperStyles: R.mergeRight(getInputWrapperStyles(props), { mb, width, maxWidth }),
  };

  if (R.equals(type, 'textarea')) {
    return (
      <InputWrapper {...wrapperStyles}>
        <Textarea {...simpleInputProps} {...G.spreadUiStyles(inputStyles)} />
      </InputWrapper>
    );
  }

  if (R.equals(type, 'password')) {
    return (
      <InputWrapper {...wrapperStyles}>
        <PasswordInput {...simpleInputProps} {...G.spreadUiStyles(inputStyles)} />
      </InputWrapper>
    );
  }

  if (R.equals(type, 'checkbox')) {
    return (
      <CheckboxWrapper {...wrapperStyles}>
        <Checkbox
          {...simpleInputProps}
          value={Boolean(value)}
          checked={Boolean(value)}
          {...G.spreadUiStyles(inputStyles)}
        />
      </CheckboxWrapper>
    );
  }

  if (R.equals(type, 'toggle')) {
    return (
      <ToggleWrapper {...wrapperStyles} title={title}>
        <Toggle
          {...simpleInputProps}
          icons={false}
          value={Boolean(value)}
          checked={Boolean(G.ifElse(
            G.isNotNilAndNotEmpty(props.checked),
            props.checked,
            props.value,
          ))}
        />
      </ToggleWrapper>
    );
  }

  if (R.equals(type, 'select')) {
    return (
      <InputWrapper {...wrapperStyles}>
        <SelectWrapper height={inputStyles.height}>
          <InputSelect {...simpleInputProps} {...G.spreadUiStyles(inputStyles)}>
            {renderOptions(options)}
          </InputSelect>
        </SelectWrapper>
      </InputWrapper>
    );
  }

  if (R.equals(type, 'reactSelect')) {
    const reactSelectProps = R.pick([
      'id',
      'value',
      'options',
      'isMulti',
      'sortable',
      'isClearable',
      'onChange',
      'isGrouped',
      'components',
      'placeholder',
      'valueComponent',
      'getOptionLabel',
      'shouldValidate',
      'formatOptionLabel',
      'closeMenuOnSelect',
      'closeMenuOnScroll',
      'shouldCustomChange',
      'withoutFixedHeight',
      'useMenuPortalTarget',
      'valueContainerMaxHeight',
      'reactSelectAdditionalStyles',
    ], props);

    const disabled = R.or(
      R.pathEq('hidden', ['inputWrapperStyles', 'visibility'], wrapperStyles),
      R.pathOr(false, ['disabled'], props),
    );

    if (G.isTrue(withClickZIndex)) {
      return (
        <InputWrapperWithClickZIndex {...wrapperStyles}>
          <Box {...G.spreadUiStyles(inputStyles)}>
            <ReactSelectInput
              {...inputStyles}
              {...reactSelectProps}
              {...formikProps}
              hasError={hasError}
              disabled={disabled}
              useMaxHeight={useMultiSelectMaxHeight}
            />
          </Box>
        </InputWrapperWithClickZIndex>
      );
    }

    return (
      <InputWrapper {...wrapperStyles}>
        <Box {...G.spreadUiStyles(inputStyles)}>
          <ReactSelectInput
            {...inputStyles}
            {...reactSelectProps}
            {...formikProps}
            hasError={hasError}
            disabled={disabled}
            useMaxHeight={useMultiSelectMaxHeight}
          />
        </Box>
      </InputWrapper>
    );
  }

  if (R.equals(type, 'asyncSearch')) {
    const reactSelectProps = R.pick([
      'id',
      'value',
      'showIcon',
      'placeholder',
      'getLoadOptions',
      'handleAsyncSearchSelectChange',
    ], props);

    const defaultInputStyles = {
      width: '100%',
    };

    const searchSelectStyles = R.mergeRight(defaultInputStyles, R.or(inputStyles, {}));

    return (
      <InputWrapper {...wrapperStyles}>
        <AsyncReactSelectInput
          {...searchSelectStyles}
          {...reactSelectProps}
          {...formikProps}
        />
      </InputWrapper>
    );
  }

  if (R.equals(type, 'multiEmail')) {
    const inputProps = R.pick([
      'id',
      'value',
      'disabled',
      'hasError',
      'shouldOverrideEmails',
    ], props);

    const defaultInputStyles = {
      minHeight: 36,
      borderRadius: '4px',
      borderColor: G.getTheme('colors.dark.grey'),
    };

    const multiEmailStyles = R.mergeRight(defaultInputStyles, R.or(inputStyles, {}));

    return (
      <InputWrapper {...wrapperStyles}>
        <MultiEmailInput
          {...inputProps}
          {...formikProps}
          {...multiEmailStyles}
          width={R.propOr(width, 'multiEmailWidth', multiEmailStyles)}
        />
      </InputWrapper>
    );
  }

  if (R.equals(type, 'multiItems')) {
    const inputProps = R.pick([
      'id',
      'value',
      'disabled',
      'placeholder',
    ], props);

    const defaultInputStyles = {
      minHeight: 36,
      borderRadius: '4px',
      borderColor: G.getTheme('colors.dark.grey'),
    };

    const multiItemsStyles = R.mergeRight(defaultInputStyles, R.or(inputStyles, {}));

    return (
      <InputWrapper {...wrapperStyles}>
        <MultiItemsInput
          {...inputProps}
          {...formikProps}
          {...multiItemsStyles}
          width={width}
        />
      </InputWrapper>
    );
  }

  if (R.equals(type, 'addressAutocomplete')) {
    const placeProps = R.pick([
      'id',
      'value',
      'handlers',
      'disabled',
      'fieldType',
      'placeholder',
      'omitAddress1',
      'locationMapper',
      'additionalStyles',
      'addressFieldName',
      'closeMenuOnScroll',
      'shouldHandleEnter',
      'useMenuPortalTarget',
      'useFormattedAddress',
      'concatLocationFields',
      'customSelectLocationFunction',
    ], props);

    return (
      <InputWrapper {...wrapperStyles}>
        <PlaceAutocomplete
          {...placeProps}
          {...formikProps}
          hasError={hasError}
          width={inputStyles.width}
          height={inputStyles.height}
        />
      </InputWrapper>
    );
  }

  if (R.equals(type, 'countrySelect')) {
    const selectProps = R.pick([
      'id',
      'value',
      'disabled',
    ], props);

    return (
      <InputWrapper {...wrapperStyles}>
        <CountrySelectWrapper {...G.spreadUiStyles(inputStyles)} hasError={hasError}>
          <CountrySelect {...selectProps} {...formikProps} />
        </CountrySelectWrapper>
      </InputWrapper>
    );
  }

  if (R.equals(type, 'datePicker')) {
    const inputProps = R.pick([
      'minDate',
      'maxDate',
      'fromPage',
      'isClearable',
      'timeSelection',
      'disablePortal',
      'handleChangeInput',
      'shouldCustomChange',
      'customChangeHandler2',
    ], props);

    const configTimeIntervals = R.or(G.getCalendarTimeIntervalConfigFromWindow(), 15);
    const configUseMuiCalendar = G.getAmousConfigByNameFromWindow(GC.UI_USE_MUI_CALENDAR);
    const configDateFormat = G.getAmousConfigByNameFromWindow(GC.GENERAL_BRANCH_DATE_TIME_FORMAT);

    if (G.isFalse(configUseMuiCalendar)) {
      return (
        <InputWrapperWithClickZIndex {...wrapperStyles}>
          <CalendarIconWrapper width={R.pathOr(width, ['width'], inputStyles)}>
            <CalendarInput
              {...props}
              {...inputStyles}
              customInput={true}
              format={configDateFormat}
              timeIntervals={configTimeIntervals}
            />
          </CalendarIconWrapper>
        </InputWrapperWithClickZIndex>
      );
    }

    return (
      <InputWrapper {...wrapperStyles}>
        <DateTimeInput
          {...simpleInputProps}
          {...formikProps}
          {...inputProps}
          inputStyles={inputStyles}
          format={configDateFormat}
          timeIntervals={configTimeIntervals}
        />
      </InputWrapper>
    );
  }

  if (R.equals(type, 'timeInput')) {
    const inputProps = R.pick([
      'fromPage',
      'handleChangeInput',
    ], props);

    const configDateFormat = G.getAmousConfigByNameFromWindow(GC.GENERAL_BRANCH_DATE_TIME_FORMAT);

    return (
      <InputWrapper {...wrapperStyles}>
        <MaskedTimeInput
          {...simpleInputProps}
          {...formikProps}
          {...inputProps}
          inputStyles={inputStyles}
          format={configDateFormat}
          wrapperStyles={wrapperStyles}
        />
      </InputWrapper>
    );
  }

  if (R.equals(type, 'phoneNumber')) {
    const { disabled, phoneInputBorder, phoneInputPaddingLeft, phoneInputBorderRadius } = props;

    return (
      <InputWrapper {...wrapperStyles}>
        <PhoneNumberInputWrapper
          {...G.spreadUiStyles(inputStyles)}
          width='100%'
          hasError={hasError}
          phoneInputBorder={phoneInputBorder}
          phoneInputPaddingLeft={phoneInputPaddingLeft}
          phoneInputBorderRadius={phoneInputBorderRadius}
        >
          <PhoneInput
            country='US'
            error={null}
            flags={flags}
            value={value}
            disabled={disabled}
            countries={PHONE_COUNTRIES}
            onBlur={() => setFieldTouched(name, true)}
            onChange={(value) => setFieldValue(name, R.or(value, ''))}
            placeholder={G.getWindowLocale('titles:enter-phone-number', 'Enter phone number')}
          />
        </PhoneNumberInputWrapper>
      </InputWrapper>
    );
  }

  if (R.equals(type, 'file')) {
    const { width, height, disabled } = props;
    const inputHeight = R.propOr(height, 'height', inputStyles);

    let fileName;

    if (G.isNotNilAndNotEmpty(value)) fileName = R.pathOr(value, [GC.FIELD_NAME], value);

    return (
      <InputWrapper
        {...wrapperStyles}
        border='1px dotted'
      >
        <Dropzone
          disabled={disabled}
          className='drop-zone'
          onDrop={(files) => setFieldValue(name, R.head(files || []))}
        >
          {({ getRootProps, getInputProps }) => (
            <Flex
              {...getRootProps()}
              px={15}
              cursor='pointer'
              borderRadius='4px'
              textAlign='center'
              width={props.width}
              border='1px dashed'
              wordBreak='break-word'
              justifyContent='center'
              height={R.or(inputHeight, 60)}
              borderColor={renderBorderColor(props)}
            >
              <input {...R.dissoc('multiple', getInputProps())} />
              Drag and Drop or select file from your Computer
            </Flex>
          )}
        </Dropzone>
        {
          G.isNotNilAndNotEmpty(fileName) &&
          <Flex px={15} mt={10} position='relative' width={width}>
            <TextComponent
              display='block'
              title={fileName}
              withEllipsis={true}
              maxWidth='calc(100% - 20px)'
            >
              {fileName}
            </TextComponent>
            {
              R.not(disabled) &&
              <Box
                ml='7px'
                cursor='pointer'
                onClick={(event) => {
                  event.preventDefault();
                  setFieldValue(name, null);
                }}
              >
                {I.trash(G.getTheme('colors.dark.blue'))}
              </Box>
            }
          </Flex>
        }
      </InputWrapper>
    );
  }

  if (R.equals(type, 'files')) {
    const { width, height } = props;
    const inputHeight = R.propOr(height, 'height', inputStyles);

    const fileNames = R.compose(
      R.filter(G.isNotNilAndNotEmpty),
      R.map(R.pathOr(null, [GC.FIELD_NAME])),
    )(R.or(value, []));

    return (
      <InputWrapper
        {...wrapperStyles}
        border='1px dotted'
      >
        <Dropzone
          className='drop-zone'
          onDrop={(files) => setFieldValue(name, R.concat(value || [], files || []))}
        >
          {({ getRootProps, getInputProps }) => (
            <Flex
              {...getRootProps()}
              px={15}
              cursor='pointer'
              borderRadius='4px'
              textAlign='center'
              width={props.width}
              border='1px dashed'
              wordBreak='break-word'
              justifyContent='center'
              height={R.or(inputHeight, 60)}
              borderColor={renderBorderColor(props)}
            >
              <input {...getInputProps()} />
              Drag and Drop or select files from your Computer
            </Flex>
          )}
        </Dropzone>
        {
          G.isNotNilAndNotEmpty(fileNames) &&
          <Flex
            px={15}
            mt={10}
            position='relative'
            width={width || '100%'}
            justifyContent='space-between'
          >
            <Flex
              flexDirection='column'
              alignItems='flex-start'
              maxWidth='calc(100% - 20px)'
            >
              {
                fileNames.map((fileName, i) => (
                  <Flex key={fileName} maxWidth='100%' justifyContent='space-between'>
                    <Box
                      mr={15}
                      cursor='pointer'
                      onClick={(event) => {
                        event.preventDefault();

                        setFieldValue(name, R.remove(i, 1, value));
                      }}
                    >
                      {I.trash(G.getTheme('colors.dark.blue'))}
                    </Box>
                    <TextComponent
                      display='block'
                      maxWidth='100%'
                      title={fileName}
                      withEllipsis={true}
                    >
                      {fileName}
                    </TextComponent>
                  </Flex>
                ))
              }
            </Flex>
            <Box
              cursor='pointer'
              onClick={(event) => {
                event.preventDefault();
                setFieldValue(name, null);
              }}
            >
              {I.trash(G.getTheme('colors.dark.blue'))}
            </Box>
          </Flex>
        }
      </InputWrapper>
    );
  }

  if (R.equals(type, 'editor')) {
    const { errors, values, touched, disabled, onChange, fieldName, shouldCustomChange } = props;

    const editorProps = {
      errors,
      touched,
      onChange,
      disabled,
      fieldName,
      wrapperStyles,
      setFieldValue,
      setFieldTouched,
      shouldCustomChange,
      value: R.pathOr('', [fieldName], values),
    };

    return <EditorComponent {...editorProps} />;
  }

  if (R.equals(type, 'colorPicker')) {
    const { fieldName } = props;

    const pickerProps = {
      value,
      fieldName,
      setFieldValue,
      itemStyles: R.path(['itemStyles'], inputStyles),
      wrapperStyles: R.path(['wrapperStyles'], inputStyles),
    };

    return (
      <InputWrapper {...wrapperStyles}>
        <ColorPicker {...pickerProps} />
      </InputWrapper>
    );
  }

  if (R.equals(type, 'customComponent')) {
    const { Component } = props;

    return (
      <InputWrapper {...wrapperStyles}>
        <Component {...props} />
      </InputWrapper>
    );
  }

  if (R.equals(type, 'multiswitch')) {
    const { options, fieldName, setFieldValue, shouldCustomChange, customChangeHandler } = props;

    const handleSwitch = (value) => {
      if (shouldCustomChange) return customChangeHandler(value, props);

      return setFieldValue(fieldName, value);
    };

    return (
      <InputWrapper {...wrapperStyles}>
        <Switcher
          {...props}
          onSwitch={(value) => handleSwitch(value)}
          selectedOptionIndex={R.findIndex(R.propEq(value, GC.FIELD_VALUE), R.or(options, []))}
        />
      </InputWrapper>
    );
  }

  if (R.equals(type, 'readonly')) {
    return (
      <InputWrapper {...wrapperStyles}>
        <Flex height={36} fontSize={12} {...G.spreadUiStyles(inputStyles)}>{value}</Flex>
      </InputWrapper>
    );
  }

  if (R.equals(type, 'radio')) {
    const { value, options, fieldName, setFieldValue, radioGroupWrapperStyles, radioGroupLabelSpanStyles } = props;

    return (
      <InputWrapper {...wrapperStyles}>
        <RadioGroup
          value={value}
          options={options}
          fieldName={fieldName}
          setFieldValue={setFieldValue}
          radioGroupWrapperStyles={radioGroupWrapperStyles}
          radioGroupLabelSpanStyles={radioGroupLabelSpanStyles}
        />
      </InputWrapper>
    );
  }

  if (R.equals(type, 'radioMui')) {
    const { value, options, fieldName, setFieldValue, radioGroupWrapperStyles, radioGroupLabelSpanStyles } = props;

    return (
      <InputWrapper {...wrapperStyles}>
        <RadioGroupMui
          value={value}
          options={options}
          fieldName={fieldName}
          setFieldValue={setFieldValue}
          radioGroupWrapperStyles={radioGroupWrapperStyles}
          radioGroupLabelSpanStyles={radioGroupLabelSpanStyles}
        />
      </InputWrapper>
    );
  }

  return (
    <InputWrapper {...wrapperStyles}>
      <Input {...simpleInputProps} {...G.spreadUiStyles(inputStyles)} />
    </InputWrapper>
  );
});

const setOptions = (item, props) => {
  const { options } = item;

  const { branchConfigs } = props;

  if (G.isArray(options)) {
    return options;
  }

  if (G.isObject(options)) {
    const { configName } = options;

    return G.createOptionsFromDropdownConfigWithGuidOrParentGuid(branchConfigs, configName, true);
  }

  if (G.isString(options)) {
    return R.prop(options, props);
  }

  return null;
};

const setDisabled = (field, values, handlers, props) => {
  let disabled = field.disabled;

  if (G.isNotNilAndNotEmpty(field.customDisabledFunction)) {
    disabled = handlers[field.customDisabledFunction](values);
  }

  if (G.isFunction(field.disabled)) {
    disabled = field.disabled(field, values, props);
  }

  return disabled;
};

const getFieldValue = (fieldName, values) => {
  if (R.isNil(fieldName)) return null;

  return R.path(R.split('.', fieldName), values);
};

const getFieldName = (item, props) => {
  const { arrayName, itemIndex, fieldsetType } = props;

  const { fieldName } = item;

  if (R.equals(fieldsetType, 'array')) {
    return `${arrayName}.${itemIndex}.${fieldName}`;
  }

  return fieldName;
};

const getFieldKey = (item, props, index) => {
  const { itemIndex, fieldsetType } = props;

  const { fieldName } = item;

  if (R.equals(fieldsetType, 'array')) {
    return `${index}.${itemIndex}.${fieldName}`;
  }

  return `${index}${fieldName}`;
};

const getFieldError = (errors, name) => getIn(errors, name);

const checkHasFieldError = (name, errors, touched) => {
  const error = getFieldError(errors, name);
  const touch = getIn(touched, name);

  return R.and(G.isNotNilAndNotEmpty(error), R.or(G.isTrue(touch), G.isArray(touch)));
};

const getChangeHandler = (item, props) => {
  const { arrayItem, fieldsetType, handleChange, handleCustomChange } = props;

  const { shouldCustomChange, customChangeHandler } = item;

  if (R.and(G.isTrue(shouldCustomChange), G.isFunction(customChangeHandler))) {
    return (event) => customChangeHandler(event, item, props);
  }

  const customChangeHandlerFromProps = G.getPropFromObject(customChangeHandler, props);

  if (G.isAllTrue(
    G.isTrue(shouldCustomChange),
    G.isFunction(customChangeHandlerFromProps),
    R.equals(fieldsetType, 'array'),
  )) {
    return (event) => customChangeHandlerFromProps(event, arrayItem, props);
  }

  if (R.and(G.isTrue(shouldCustomChange), G.isFunction(handleCustomChange))) {
    return (e) => handleCustomChange(e, item.fieldName);
  }

  return handleChange;
};

const getBlurHandler = (item, props) => {
  const { handleBlur, setFieldValue } = props;

  const { shouldCustomBlur, customBlurHandler } = item;

  if (R.and(G.isTrue(shouldCustomBlur), G.isFunction(customBlurHandler))) {
    return (event) => customBlurHandler(event, item, props);
  }

  return (e) => {
    handleBlur(e);

    const { type, fieldName } = item;
    // This is necessary to remove more than one space in a row and trim for a text type field.
    if (R.or(R.isNil(type), R.equals(type, 'text'))) {
      const value = R.path(['currentTarget', 'value'], e);

      if (R.isEmpty(value)) return;

      const newValue = R.trim(R.replace(/\s{2,}/g, ' ', value));

      setFieldValue(fieldName, newValue);
    }
  };
};

const FieldsetWrapper = ({
  children,
  isDesktop,
  fieldsWrapperStyles,
}) => {
  if (isDesktop) {
    return (
      <Flex flexWrap='wrap' {...G.spreadUiStyles(fieldsWrapperStyles)}>
        {children}
      </Flex>
    );
  }

  const { gridRowGap, gridColumnGap, gridTemplateColumns } = fieldsWrapperStyles;

  return (
    <Grid
      {...G.spreadUiStyles(fieldsWrapperStyles)}
      gridRowGap={gridRowGap}
      gridColumnGap={gridColumnGap}
      gridTemplateColumns={gridTemplateColumns}
    >
      {children}
    </Grid>
  );
};

const Fieldset2 = (props) => {
  const {
    fields,
    values,
    errors,
    grouped,
    touched,
    errorTop,
    handlers,
    fromPage,
    setErrors,
    setValues,
    arrayItem,
    calcZIndex,
    inputStyles,
    arrayLength,
    rowMapIndex,
    fieldsetType,
    setFieldError,
    setFieldValue,
    branchConfigs,
    getLoadOptions,
    setFieldTouched,
    isDesktop = true,
    handleChangeInput,
    fieldsWrapperStyles,
    customSelectLocationFunction,
    handleAsyncSearchSelectChange,
    additionFieldComponentProps = [],
  } = props;

  if (G.isTrue(grouped)) {
    return (
      <Fragment>
        {
          fields.map((item, index) => (
            <Flex
              key={index}
              flexWrap='wrap'
              {...G.spreadUiStyles(R.or(G.getPropFromObject('fieldsWrapperStyles', item), fieldsWrapperStyles))}
            >
              {
                item.fields.map((item, index) => {
                  const fieldName = getFieldName(item, props);
                  const fieldError = getFieldError(errors, fieldName);
                  const hasError = checkHasFieldError(fieldName, errors, touched);
                  const key = getFieldKey(item, props, index);
                  const changeHandler = getChangeHandler(item, props);
                  const blurHandler = getBlurHandler(item, props);
                  const mergedInputStyles = R.mergeRight(
                    R.or(inputStyles, {}),
                    R.propOr({}, 'inputStyles', item),
                  );

                  return (
                    <FieldComponent
                      {...item}
                      {...R.pick(additionFieldComponentProps, props)}
                      key={key}
                      id={fieldName}
                      errors={errors}
                      values={values}
                      name={fieldName}
                      touched={touched}
                      error={fieldError}
                      handlers={handlers}
                      hasError={hasError}
                      fromPage={fromPage}
                      onBlur={blurHandler}
                      arrayItem={arrayItem}
                      isDesktop={isDesktop}
                      setErrors={setErrors}
                      setValues={setValues}
                      calcZIndex={calcZIndex}
                      onChange={changeHandler}
                      arrayLength={arrayLength}
                      rowMapIndex={rowMapIndex}
                      fieldsetType={fieldsetType}
                      setFieldError={setFieldError}
                      setFieldValue={setFieldValue}
                      branchConfigs={branchConfigs}
                      inputStyles={mergedInputStyles}
                      getLoadOptions={getLoadOptions}
                      setFieldTouched={setFieldTouched}
                      options={setOptions(item, props)}
                      handleChangeInput={handleChangeInput}
                      value={getFieldValue(fieldName, values)}
                      disabled={setDisabled(item, values, handlers, props)}
                      // TODO: check moving to additionFieldComponentProps
                      customSelectLocationFunction={customSelectLocationFunction}
                      handleAsyncSearchSelectChange={handleAsyncSearchSelectChange}
                    />
                  );
                })
              }
            </Flex>
          ))
        }
      </Fragment>
    );
  }

  return (
    <FieldsetWrapper {...props} isDesktop={isDesktop}>
      {
        fields.map((item, index) => {
          const fieldName = getFieldName(item, props);
          const fieldError = getFieldError(errors, fieldName);
          const hasError = checkHasFieldError(fieldName, errors, touched);
          const key = getFieldKey(item, props, index);
          const changeHandler = getChangeHandler(item, props);
          const blurHandler = getBlurHandler(item, props);
          const mergedInputStyles = R.mergeRight(
            R.or(inputStyles, {}),
            R.propOr({}, 'inputStyles', item),
          );

          return (
            <FieldComponent
              {...item}
              {...R.pick(additionFieldComponentProps, props)}
              key={key}
              id={fieldName}
              errors={errors}
              values={values}
              name={fieldName}
              touched={touched}
              error={fieldError}
              handlers={handlers}
              hasError={hasError}
              fromPage={fromPage}
              errorTop={errorTop}
              onBlur={blurHandler}
              arrayItem={arrayItem}
              isDesktop={isDesktop}
              setErrors={setErrors}
              setValues={setValues}
              calcZIndex={calcZIndex}
              onChange={changeHandler}
              arrayLength={arrayLength}
              rowMapIndex={rowMapIndex}
              fieldsetType={fieldsetType}
              setFieldError={setFieldError}
              setFieldValue={setFieldValue}
              branchConfigs={branchConfigs}
              inputStyles={mergedInputStyles}
              getLoadOptions={getLoadOptions}
              setFieldTouched={setFieldTouched}
              options={setOptions(item, props)}
              handleChangeInput={handleChangeInput}
              value={getFieldValue(fieldName, values)}
              disabled={setDisabled(item, values, handlers, props)}
              // TODO: check moving to additionFieldComponentProps
              customSelectLocationFunction={customSelectLocationFunction}
              handleAsyncSearchSelectChange={handleAsyncSearchSelectChange}
            />
          );
        })
      }
    </FieldsetWrapper>
  );
};

export {
  Fieldset2,
  getFieldError,
  checkHasFieldError,
};
